import { useMemo } from "react";
import { useAccess } from "../../../../utils/hooks/access";
import { useActionsColumn } from "../../components/TimeSheetTable/useColumns/useActionsColumn";
import { useCustomFieldsColumns } from "../../components/TimeSheetTable/useColumns/useCustomFieldsColumns";
import { useDateColumn } from "../../components/TimeSheetTable/useColumns/useDateColumn";
import { useDateDurationColumn } from "../../components/TimeSheetTable/useColumns/useDateDurationColumn";
import { useDateGroupActionColumn } from "../../components/TimeSheetTable/useColumns/useDateGroupActionColumn";
import { useDateTotalColumn } from "../../components/TimeSheetTable/useColumns/useDateTotalColumn";
import { useDurationColumn } from "../../components/TimeSheetTable/useColumns/useDurationColumn";
import { useGroupsColumns } from "../../components/TimeSheetTable/useColumns/useGroupsColumns";
import { useHourTypeColumn } from "../../components/TimeSheetTable/useColumns/useHourTypeColumn";
import { useInColumn } from "../../components/TimeSheetTable/useColumns/useInColumn";
import { useJobColumn } from "../../components/TimeSheetTable/useColumns/useJobColumn";
import { useLocationColumn } from "../../components/TimeSheetTable/useColumns/useLocationColumn";
import { useOtherDurationColumn } from "../../components/TimeSheetTable/useColumns/useOtherDurationColumn";
import { useOutColumn } from "../../components/TimeSheetTable/useColumns/useOutColumn";
import { useOvernightColumn } from "../../components/TimeSheetTable/useColumns/useOvernightColumn";
import { usePayCodeColumn } from "../../components/TimeSheetTable/useColumns/usePayCodeColumn";
import { usePayCodeDescriptionColumn } from "../../components/TimeSheetTable/useColumns/usePayCodeDescriptionColumn";
import { useRateColumn } from "../../components/TimeSheetTable/useColumns/useRateColumn";
import { useScheduledAmountColumn } from "../../components/TimeSheetTable/useColumns/useScheduledAmountColumn";
import { useScheduledHoursColumn } from "../../components/TimeSheetTable/useColumns/useScheduledHoursColumn";
import { useTotalColumn } from "../../components/TimeSheetTable/useColumns/useTotalColumn";

export const useColumns = ({
    user,
    canEdit,
    dateIdMap,
    dateTimesheetMap,
    viewSummary,
    edit,
    view,
    remove,
    approve,
    unapprove,
    lock,
    unlock,
    copy,
    scheduleInfo,
    clockExceptions,
    isMyTimesheet = false,
    waive,
    openMissPunchModal,
    openInOutDateModal,
    canViewRates,
}) => {
    const { hasAccess: canViewHistory } = useAccess(
        isMyTimesheet
            ? "timeSheet.canViewHistoryMyTimesheet"
            : "timeSheet.canViewHistory"
    );

    const { hasAccess: canViewExceptions } = useAccess(
        isMyTimesheet
            ? "timeSheet.canViewClockExceptionsMyTimesheet"
            : "timeSheet.canViewClockExceptions"
    );

    const { hasAccess: canCreateRequest } = useAccess("request.canCreate");

    const dateGroupActionColumn = useDateGroupActionColumn({
        dateIdMap,
        dateTimesheetMap,
        approve,
        unapprove,
        lock,
        unlock,
        copy,
    });

    const dateColumn = useDateColumn({ dateTimesheetMap, view, viewSummary, waive, isMyTimesheet });
    const inColumn = useInColumn({ user, canEdit, clockExceptions, view: canViewExceptions && view, openInOutDateModal });
    const outColumn = useOutColumn({
        user,
        canEdit,
        clockExceptions,
        view:
            canViewExceptions && view,
        openInOutDateModal,
        openMissPunchModal: canCreateRequest ? openMissPunchModal : undefined,
        isMyTimesheet,
    });
    const overnightColumn = useOvernightColumn();
    const rateColumn = useRateColumn({ canEdit });
    const payCodeColumn = usePayCodeColumn({ canEdit, user });
    const payCodeDescriptionColumn = usePayCodeDescriptionColumn({ canEdit, user });
    const hourTypeColumn = useHourTypeColumn();
    const durationColumn = useDurationColumn({ canEdit });
    const otherDurationColumn = useOtherDurationColumn({ canEdit, canViewRates });
    const dateDurationColumn = useDateDurationColumn({ dateIdMap, dateTimesheetMap });
    const dateTotalColumn = useDateTotalColumn({ dateIdMap, dateTimesheetMap });
    const totalColumn = useTotalColumn({ canEdit, canViewRates });
    const locationColumn = useLocationColumn({ canEdit });
    const jobColumn = useJobColumn({ canEdit });
    const groupColumns = useGroupsColumns({ canEdit });
    const customFieldsColumns = useCustomFieldsColumns({ canEdit });
    const actionsColumn = useActionsColumn({ edit, remove, isMyTimesheet });
    const scheduledHoursColumn = useScheduledHoursColumn({ dateIdMap, dateTimesheetMap, scheduleInfo, view: canViewHistory && view });
    const scheduledAmountColumn = useScheduledAmountColumn({ dateTimesheetMap, scheduleInfo });

    return useMemo(
        () =>
            [
                dateGroupActionColumn,
                dateColumn,
                inColumn,
                outColumn,
                overnightColumn,
                ...(canViewRates ? [rateColumn] : []),
                payCodeColumn,
                payCodeDescriptionColumn,
                hourTypeColumn,
                durationColumn,
                otherDurationColumn,
                dateDurationColumn,
                scheduledHoursColumn,
                totalColumn,
                ...(canViewRates ? [dateTotalColumn, scheduledAmountColumn] : []),
                locationColumn,
                jobColumn,
                ...groupColumns,
                ...customFieldsColumns,
                (edit || remove || (openMissPunchModal && canCreateRequest)) ? actionsColumn : null,
            ]?.filter((c) => !!c),
        [
            dateGroupActionColumn,
            dateColumn,
            inColumn,
            outColumn,
            rateColumn,
            durationColumn,
            otherDurationColumn,
            payCodeColumn,
            payCodeDescriptionColumn,
            hourTypeColumn,
            overnightColumn,
            totalColumn,
            dateTotalColumn,
            dateDurationColumn,
            locationColumn,
            jobColumn,
            groupColumns,
            customFieldsColumns,
            actionsColumn,
            scheduledHoursColumn,
            edit,
            remove,
            scheduledAmountColumn,
            openMissPunchModal,
            canCreateRequest,
            canViewRates,
        ],
    );
};
